<template>
  <div class="container py-16 mb-10">
    <form class="bg-white rounded-3xl shadow-md py-8 md:py-16 px-7 md:px-16">
      <h1
        class="font-display text-primary text-lg lg:text-2xl xl:text-3xl font-bold mb-10"
      >
        {{ $t("requestPartner.title") }}
      </h1>
      <div class="pb-5">
        <CoreInput
          id="company"
          v-model="form.company"
          :placeholder="$t('requestPartner.form.company')"
          :label="$t('requestPartner.form.company') + ' *'"
        />
        <CoreInputError :error="get(errors, 'company')" />
      </div>
      <div class="pb-5">
        <CoreInput
          id="referral"
          v-model="form.referral"
          :placeholder="$t('requestPartner.form.name')"
          :label="$t('requestPartner.form.name') + ' *'"
        />
        <CoreInputError :error="get(errors, 'referral')" />
      </div>
      <div class="pb-5">
        <CoreInput
          id="role"
          v-model="form.role"
          :placeholder="$t('requestPartner.form.role')"
          :label="$t('requestPartner.form.role') + ' *'"
        />
        <CoreInputError :error="get(errors, 'role')" />
      </div>
      <div class="pb-5">
        <CoreInput
          id="email"
          v-model="form.email"
          :placeholder="$t('requestPartner.form.companyEmail')"
          :label="$t('requestPartner.form.companyEmail') + ' *'"
        />
        <CoreInputError :error="get(errors, 'email')" />
      </div>
      <div class="pb-10">
        <CoreInput
          id="phone_number"
          v-model="form.phone_number"
          :placeholder="$t('requestPartner.form.phone')"
          :label="$t('requestPartner.form.phone') + ' *'"
        />
        <CoreInputError :error="get(errors, 'phone_number')" />
      </div>
      <div class="mb-16">
        <CoreCheckbox v-model="form.terms"
          ><span class="text-sm text-brand-gray"
            >{{ $t("requestPartner.form.gdpr") }} *</span
          >
        </CoreCheckbox>
        <CoreInputError :error="get(errors, 'terms')" />
      </div>
      <CoreButton
        class="px-20 mb-10 md:mb-0 w-full"
        :disabled="sending ? true : false"
        @click="handleSubmit()"
        type="button"
        >{{ $t("requestPartner.form.button") }}</CoreButton
      >

      <p v-if="error" class="text-red-600 mt-3">{{ error }}</p>
      <p v-if="notices" v-for="notice in notices" class="text-green-600 mt-3">
        {{ notice }}
      </p>
    </form>
  </div>
</template>

<script setup lang="ts">
import { get, forEach, cloneDeep, set } from "lodash-es";
import * as yup from "yup";

const { t, locale } = useI18n();
const { send, sending, error } = useMail();

const schema = yup.object({
  company: yup
    .string()
    .required(
      t("validation.required", { field: t("requestPartner.form.company") }),
    )
    .label(t("requestPartner.form.company")),
  referral: yup
    .string()
    .required(
      t("validation.required", { field: t("requestPartner.form.name") }),
    )
    .label(t("requestPartner.form.name")),
  role: yup
    .string()
    .required(
      t("validation.required", { field: t("requestPartner.form.role") }),
    )
    .label(t("requestPartner.form.role")),
  email: yup
    .string()
    .required(
      t("validation.required", {
        field: t("requestPartner.form.companyEmail"),
      }),
    )
    .label(t("requestPartner.form.companyEmail")),
  phone_number: yup
    .string()
    .trim()
    .required(
      t("validation.required", { field: t("requestPartner.form.phone") }),
    )
    .matches(/^\d{10}$/, {
      excludeEmptyString: true,
      message: t("validation.phone.error"),
    })
    .label(t("requestPartner.form.phone")),
  terms: yup.bool().oneOf([true], t("validation.requiredField")),
});

const form = reactive({
  company: "",
  referral: "",
  role: "",
  email: "",
  phone_number: "",
  terms: false,
});

const isValid = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const errors = reactive<Record<string, any>>({});
const notices = ref<string[]>([]);

const handleSubmit = async () => {
  isLoading.value = true;
  notices.value = [];

  //validation
  await validate();

  if (!isValid.value) {
    isLoading.value = false;
    return;
  }

  const sent = await send(
    {
      subject: t("requestPartner.email.subject"),
      html: `<strong>${t("requestPartner.form.company")}: ${form.company}<br> ${t("requestPartner.form.name")}: ${form.referral}<br> ${t("requestPartner.form.role")}: ${form.role}<br> ${t("requestPartner.form.companyEmail")}: ${form.email}<br> ${t("requestPartner.form.phone")}: ${form.phone_number}`,
    }
  );

  if (sent) {
    notices.value.push(t("requestPartner.form.success"));

    forEach(form, (value: string, key: string) => {
      form[key] = "";
    });

    errors.value = {};
  }
};

const validate = async () => {
  try {
    errors.value = {};
    const validatedValues = await schema.validate(cloneDeep(form), {
      abortEarly: false,
    });
    isValid.value = true;
    return validatedValues;
  } catch (error) {
    setErrors(error);
    isValid.value = false;
    isLoading.value = false;
    return null;
  }
};

const setErrors = (e: any) => {
  if (!e) {
    return;
  }

  Object.keys(errors).forEach((key) => delete errors[key]);

  if (Array.isArray(e)) {
    e.forEach((validationError) => {
      set(errors, validationError.path, validationError.message);
    });
    return;
  }

  if (!e.inner) {
    return;
  }

  e.inner.forEach((validationError: any) => {
    set(errors, validationError.path, validationError.message);
  });
};
</script>
